import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fromState } from "@/lib/nav";
import { RememberedSearchInput, SearchCameraButton } from "@/components/search/engine";
import { SynonymSuggestButton } from "@/components/search/SynonymSuggest";
import { Search as SearchIcon, X, History, Package, Truck, StickyNote } from "lucide-react";

// Which tab you were on comes back with you from a part or a truck (this browser tab only).
export function useSessionTab(page, def) {
  const key = `fas.tab.${page}`;
  const [tab, setTab] = useState(() => sessionStorage.getItem(key) || def);
  useEffect(() => { sessionStorage.setItem(key, tab); }, [key, tab]);
  return [tab, setTab];
}

// ENTER AND SCANNING — HIS RULE (26 Oct). A scan, or one of OUR OWN tag numbers typed exactly (a part's FAS tag
// number, a truck's stock number), opens that item, whichever of the three screens you're on: it can only ever be
// one thing. Everything else typed NEVER jumps to a result, even with one match: you get the list to fine-tune.
export function useOwnTagOpen(from, boxTestid) {
  const navigate = useNavigate();
  const location = useLocation();
  const ownTag = useOwnTag(boxTestid);
  return useCallback(async (t) => {
    const hit = await ownTag(t);
    if (hit) navigate(hit.kind === "part" ? `/parts/${hit.id}` : `/donors/${hit.id}`, { state: fromState(location, from) });
    return hit;
  }, [navigate, location, from, ownTag]);
}

// The same exact-own-tag answer without moving: a picker PICKS what it holds (S4). null = an ordinary search.
export function useOwnTag(boxTestid) {
  return useCallback(async (t) => {
    if (!t || /\s/.test(t)) return null;
    const box = document.querySelector(`[data-testid="${boxTestid}"]`);
    const atEnter = box?.value;
    const hit = await api.get("/search/own-tag", { params: { code: t } }).then((r) => r.data).catch(() => null);
    if (!hit?.kind) return null;
    // typed on since Enter? then the answer is for words no longer in the box — stay
    if (box && box.value !== atEnter && box.value.trim() !== t) return null;
    return hit;
  }, [boxTestid]);
}

// A search that sat on screen for 3 seconds is one you meant — it goes on your recent list.
export function useRecentSettle(recent, settledFor) {
  const { add } = recent;
  useEffect(() => {
    const v = (settledFor || "").trim();
    if (!v) return undefined;
    const t = setTimeout(() => add(v), 3000);
    return () => clearTimeout(t);
  }, [settledFor, add]);
}

// THE ONE BOX (Search S3): on top of the results at every width, gloves-sized. Enter (a scan gun's Enter, or the
// camera) hands the typed value to `onEnter` (see useOwnTagOpen for what opens). Your recent searches sit under it.
export function SearchBox({ value, onChange, onEnter, onPick, testid = "search-input", placeholder, recent, children }) {
  return (
    <div data-testid={`${testid}-box`} className="mb-3">
      {/* the camera sits BESIDE the form, not in it: its panel has its own form, and a form inside a form reloads the page */}
      <div className="flex items-center gap-2">
        <form className="relative flex-1 min-w-0 flex items-center gap-2" onSubmit={(e) => {
          e.preventDefault();
          // S5: Enter or the Search button runs it — the same hand-over either way
          const v = e.currentTarget.querySelector("input")?.value || "";
          onChange(v.trim() ? v : "");
          onEnter(v);
        }}>
          <div className="relative flex-1 min-w-0">
          <SearchIcon className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <RememberedSearchInput testid={testid} placeholder={placeholder} value={value} onChange={onChange}
            enterKeyHint="search" autoComplete="off"
            className="rounded-xl border-slate-300 bg-white pl-11 pr-12 h-12 text-base shadow-sm" />
          {value && (
            <button type="button" data-testid={`${testid}-clear`} onClick={() => onChange("")} aria-label="Clear the search"
              className="absolute right-0 top-0 h-12 w-12 flex items-center justify-center text-slate-400 hover:text-slate-800 transition-colors">
              <X className="w-5 h-5" />
            </button>
          )}
          </div>
          <button type="submit" data-testid={`${testid}-go`} aria-label="Search"
            className="shrink-0 h-12 rounded-xl bg-[#B01E28] hover:bg-[#8B1820] px-5 text-base font-bold text-white inline-flex items-center gap-2 shadow-sm transition-colors">
            <SearchIcon className="w-5 h-5" /><span className="hidden sm:inline">Search</span>
          </button>
        </form>
        <SearchCameraButton testid={testid} onScan={(v) => onEnter(v)} size="h-12" />
      </div>
      {recent && <RecentSearches recent={recent} onPick={onPick || onChange} testid={`${testid}-recent`} />}
      {children}
    </div>
  );
}

export function RecentSearches({ recent, onPick, testid }) {
  if (!recent.items.length) return null;
  return (
    <div data-testid={testid} className="mt-2 flex items-center gap-2 overflow-x-auto pb-1">
      <span className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-slate-400">
        <History className="w-4 h-4" />Recent
      </span>
      {recent.items.map((q, i) => (
        <span key={q} className="shrink-0 inline-flex items-center rounded-full border border-slate-300 bg-white shadow-sm">
          <button type="button" data-testid={`${testid}-${i}`} onClick={() => onPick(q)}
            className="h-12 pl-4 pr-1 text-sm font-medium text-slate-700 hover:text-[#B01E28] transition-colors max-w-[14rem] truncate">{q}</button>
          <button type="button" data-testid={`${testid}-${i}-remove`} onClick={() => recent.remove(q)}
            aria-label={`Remove ${q} from recent searches`}
            className="h-12 w-10 flex items-center justify-center text-slate-300 hover:text-slate-700 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </span>
      ))}
    </div>
  );
}

const TABS = { parts: { label: "Parts", Icon: Package }, trucks: { label: "Trucks", Icon: Truck } };

// Under the parts box: notes on request, and "the crew calls it something else" (both were inside the filter rail).
export function BoxExtras({ engine }) {
  const { f, searchNotes, setSearchNotes } = engine;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <button type="button" data-testid="toggle-search-notes" onClick={() => setSearchNotes(!searchNotes)}
        className={`inline-flex items-center gap-1.5 rounded-full h-12 px-4 text-sm font-medium border transition-colors ${searchNotes ? "border-amber-300 bg-amber-50 text-amber-800" : "border-slate-200 bg-white text-slate-600 hover:border-slate-400"}`}>
        <StickyNote className="w-4 h-4" />Also search notes{searchNotes ? " · ON" : ""}
      </button>
      <SynonymSuggestButton testid="suggest-synonym" word={f.q || ""}
        className="inline-flex items-center gap-1.5 rounded-full h-12 px-4 text-sm font-medium border border-slate-200 bg-white text-slate-600 hover:border-slate-400 transition-colors" />
    </div>
  );
}

// PARTS (N) | TRUCKS (N) — one search, two answers, each with an honest count (Search S3).
export function SearchTabs({ tab, onTab, counts, order = ["parts", "trucks"], testid = "search-tab" }) {
  return (
    <div role="tablist" data-testid={`${testid}s`} className="mb-3 flex items-stretch gap-1 rounded-xl border border-slate-200 bg-slate-100 p-1">
      {order.map((k) => {
        const { label, Icon } = TABS[k];
        const on = tab === k;
        const c = counts[k];
        return (
          <button key={k} type="button" role="tab" aria-selected={on} data-testid={`${testid}-${k}`} onClick={() => onTab(k)}
            className={`flex-1 h-12 rounded-lg inline-flex items-center justify-center gap-2 text-base font-semibold transition-colors ${on ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>
            <Icon className={`w-5 h-5 ${on ? "text-[#B01E28]" : ""}`} />{label}
            <span data-testid={`${testid}-${k}-count`}
              className={`font-mono text-sm rounded-md px-2 py-0.5 ${on ? "bg-[#B01E28] text-white" : "bg-white text-slate-600"}`}>
              {c?.count == null ? "…" : `${c.count.toLocaleString()}${c.lowerBound ? "+" : ""}`}
            </span>
          </button>
        );
      })}
    </div>
  );
}
